import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import LicenseStatus from '../components/LicenseStatus';
import LicenseRegistration from '../components/LicenseRegistration';

export default function License() {
  const [showRegistration, setShowRegistration] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState<string>('unknown');

  const handleRegistrationComplete = (data: any) => {
    setShowRegistration(false);
    // Optional: Weiterleitung oder andere Aktionen nach erfolgreicher Registrierung
  };

  const handleStatusChange = (status: string) => {
    setLicenseStatus(status);
  };

  const needsRegistration = licenseStatus === 'installation_not_found' || licenseStatus === 'unknown';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span>Lizenz-Verwaltung</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Verwalten Sie Ihre Lernrakete-Lizenz und Installation
          </p>
        </div>
      </div>

      {/* Lizenzstatus */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Aktueller Lizenzstatus</h2>
          <button
            onClick={() => setShowRegistration(!showRegistration)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showRegistration ? 'Status anzeigen' : 'Registrierung anzeigen'}
          </button>
        </div>

        {showRegistration ? (
          <LicenseRegistration
            onRegistrationComplete={handleRegistrationComplete}
            onCancel={() => setShowRegistration(false)}
          />
        ) : (
          <LicenseStatus
            showDetails={true}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>

      {/* Lizenz-Informationen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lizenztypen */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Info className="w-5 h-5 text-blue-600" />
            <span>Verfügbare Lizenzen</span>
          </h3>
          
          <div className="space-y-4">
            {/* Trial-Lizenz */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-blue-900">Trial-Lizenz</h4>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Kostenlos
                </span>
              </div>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• 30 Tage kostenlose Nutzung</li>
                <li>• Alle Grundfunktionen</li>
                <li>• AI-Assistant inklusive</li>
                <li>• Bis zu 10 Benutzer</li>
                <li>• Vollständiger Support</li>
              </ul>
            </div>

            {/* Pro-Lizenz */}
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-green-900">Pro-Lizenz</h4>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Premium
                </span>
              </div>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Unbegrenzte Nutzung</li>
                <li>• Alle Features verfügbar</li>
                <li>• Bis zu 100 Benutzer</li>
                <li>• API-Zugang</li>
                <li>• Prioritäts-Support</li>
              </ul>
            </div>

            {/* Enterprise-Lizenz */}
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-purple-900">Enterprise-Lizenz</h4>
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                  Enterprise
                </span>
              </div>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Unbegrenzte Benutzer</li>
                <li>• Custom Branding</li>
                <li>• SLA-Garantie</li>
                <li>• Dedizierter Support</li>
                <li>• On-Premise Option</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Support & Hilfe */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <span>Support & Hilfe</span>
          </h3>
          
          <div className="space-y-4">
            {/* Häufige Probleme */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Häufige Probleme</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Lizenz abgelaufen → Kontaktieren Sie den Support</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Installation nicht gefunden → Registrierung durchführen</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Features nicht verfügbar → Lizenz-Typ prüfen</span>
                </div>
              </div>
            </div>

            {/* Kontakt */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Kontakt</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">E-Mail:</span>{' '}
                  <a href="mailto:support@lernrakete.cloud" className="text-blue-600 hover:text-blue-800">
                    support@lernrakete.cloud
                  </a>
                </div>
                <div>
                  <span className="font-medium">Dokumentation:</span>{' '}
                  <a href="https://docs.lernrakete.cloud" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    docs.lernrakete.cloud
                  </a>
                </div>
                <div>
                  <span className="font-medium">Support-Zeiten:</span> Mo-Fr 9:00-17:00
                </div>
              </div>
            </div>

            {/* Lizenz verlängern */}
            {licenseStatus === 'expired' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Lizenz verlängern</h4>
                <p className="text-sm text-yellow-800 mb-3">
                  Ihre Lizenz ist abgelaufen. Kontaktieren Sie uns für eine Verlängerung.
                </p>
                <a
                  href="mailto:support@lernrakete.cloud?subject=Lizenzverlängerung"
                  className="inline-flex items-center px-3 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700"
                >
                  Lizenz verlängern
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Registrierungshinweis */}
      {needsRegistration && !showRegistration && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">
                Installation nicht registriert
              </h4>
              <p className="text-sm text-blue-800 mb-3">
                Ihre Lernrakete-Installation ist noch nicht beim Lizenzserver registriert. 
                Registrieren Sie sich jetzt, um eine kostenlose Trial-Lizenz zu erhalten.
              </p>
              <button
                onClick={() => setShowRegistration(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                <Shield className="w-4 h-4 mr-2" />
                Jetzt registrieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
